import { HttpClient, HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { DEFAULT } from '../../../../../common/models/constants';
import { MainEnvService } from '../../../../../common/shared/main-env.service';
import { StatisticsDateError, StatisticsDateRange, validateStatisticsRange } from './statistics-dates';

@Component({
  selector: 'app-statistics-detail-dialog',
  template: `
    <h2 mat-dialog-title>{{ data.label }}</h2>
    <mat-dialog-content class="mat-typography px-6">
      <div class="flex items-center gap-4 mb-2">
        <mat-form-field appearance="outline" class="flex-1 statistics-date-field">
          <mat-label>{{'statistics.dateFrom' | translate}}</mat-label>
          <input matInput type="date" required [max]="dateTo" [(ngModel)]="dateFrom" (input)="onDateInput($event, 'from')" (keyup.enter)="applyDates()" data-cy="statistics-detail-date-from">
        </mat-form-field>

        <mat-form-field appearance="outline" class="flex-1 statistics-date-field">
          <mat-label>{{'statistics.dateTo' | translate}}</mat-label>
          <input matInput type="date" required [min]="dateFrom" [(ngModel)]="dateTo" (input)="onDateInput($event, 'to')" (keyup.enter)="applyDates()" data-cy="statistics-detail-date-to">
        </mat-form-field>

        <button mat-raised-button color="primary" [disabled]="!!dateError || !datesChanged" (click)="applyDates()" data-cy="statistics-detail-update">{{'statistics.update' | translate}}</button>
      </div>
      <div *ngIf="dateError" class="text-sm mb-4" style="color: var(--error-500)" data-cy="statistics-detail-date-error">{{'statistics.dateErrors.' + dateError | translate}}</div>
      <div *ngIf="!dateError" class="text-sm text-gray-500 mb-4" data-cy="statistics-detail-range-info">
        {{'statistics.appliedRange' | translate: appliedRange}}
        <span *ngIf="datesChanged"> · {{'statistics.datesChanged' | translate}}</span>
      </div>

      <mat-form-field appearance="fill" class="w-full mb-4">
        <input matInput #searchInput [placeholder]="'statistics.searchOrg' | translate">
        <mat-icon svgIcon="search" class="p-4" matPrefix></mat-icon>
      </mat-form-field>

      <div class="shadow-dark">
        <table mat-table [dataSource]="dataSource" class="w-full">
          <ng-container matColumnDef="organizationName">
            <th mat-header-cell *matHeaderCellDef>{{'statistics.columnOrg' | translate}}</th>
            <td mat-cell *matCellDef="let element">{{ element.organizationName }}</td>
          </ng-container>

          <ng-container matColumnDef="value">
            <th mat-header-cell *matHeaderCellDef>{{ data.label }}</th>
            <td mat-cell *matCellDef="let element">{{ element.value }}</td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let element; columns: displayedColumns;"></tr>
        </table>

        <div class="flex justify-between bg-color pb-5">
          <mat-paginator (page)="getPage($event)" [length]="totalElements" [pageSizeOptions]="PAGE_SIZE_OPTIONS" showFirstLastButtons [ngClass]="{'hidden': !dataSource.data.length}" class="w-full"></mat-paginator>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [disabled]="!!dateError" (click)="downloadCsv()">{{'statistics.downloadCsv' | translate}}</button>
      <button mat-button mat-dialog-close>{{'statistics.close' | translate}}</button>
    </mat-dialog-actions>
  `,
})
export class StatisticsDetailDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  dataSource: MatTableDataSource<Record<string, any>> = new MatTableDataSource<Record<string, any>>([]);
  displayedColumns: string[] = ['organizationName', 'value'];
  PAGE_SIZE_OPTIONS = DEFAULT.PAGE_SIZE_OPTIONS;
  totalElements = 0;
  dateFrom: string;
  dateTo: string;
  /** Range the table is showing; search and pagination keep using it until the user applies new dates. */
  appliedRange: StatisticsDateRange;
  private badInput = { from: false, to: false };
  private dataSubscription?: Subscription;
  private searchSubscription?: Subscription;

  get dateError(): StatisticsDateError | null {
    return validateStatisticsRange(this.dateFrom, this.dateTo, this.badInput.from || this.badInput.to);
  }

  get datesChanged(): boolean {
    return this.appliedRange.dateFrom !== this.dateFrom || this.appliedRange.dateTo !== this.dateTo;
  }

  onDateInput(event: Event, field: 'from' | 'to'): void {
    this.badInput[field] = (event.target as HTMLInputElement).validity.badInput;
  }

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('searchInput') searchInput!: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { metric: string; label: string; dateFrom: string; dateTo: string },
    private http: HttpClient,
    private env: MainEnvService,
  ) {
    this.dateFrom = data.dateFrom;
    this.dateTo = data.dateTo;
    this.appliedRange = { dateFrom: data.dateFrom, dateTo: data.dateTo };
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit() {
    this.searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(debounceTime(250)).subscribe(() => {
      if (this.paginator) this.paginator.pageIndex = 0;
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
  }

  applyDates(): void {
    if (this.dateError) return;
    this.appliedRange = { dateFrom: this.dateFrom, dateTo: this.dateTo };
    if (this.paginator) this.paginator.pageIndex = 0;
    this.loadData();
  }

  loadData(): void {
    const search = this.searchInput?.nativeElement.value;
    const offset = this.paginator ? this.paginator.pageIndex * this.paginator.pageSize : 0;
    const limit = this.paginator ? this.paginator.pageSize : this.PAGE_SIZE_OPTIONS[0];

    let params = new HttpParams()
      .set('page[offset]', String(offset))
      .set('page[limit]', String(limit))
      .set('filter[dateFrom]', this.appliedRange.dateFrom)
      .set('filter[dateTo]', this.appliedRange.dateTo);
    if (search) params = params.set('filter[search]', search);

    // A previous query still in flight would otherwise overwrite this one's result when it lands.
    this.dataSubscription?.unsubscribe();
    this.dataSubscription = this.http.get<any>(`${this.env.url}/statistics/${this.data.metric}/detail`, { params })
      .subscribe(res => {
        const items = res.data ?? [];
        this.dataSource.data = items.map((item: any) => item.attributes ?? item);
        this.totalElements = res.meta?.total ?? res.total ?? 0;
      });
  }

  getPage(_event: PageEvent): void {
    this.loadData();
  }

  downloadCsv(): void {
    if (this.dateError) return;
    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;
    const params = new HttpParams().set('dateFrom', dateFrom).set('dateTo', dateTo);

    this.http.get(`${this.env.url}/statistics/csv`, {
      params,
      responseType: 'blob',
    }).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistics-${this.data.metric}-${dateFrom}_${dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }
}
